import { useEffect, useRef } from 'react'
import mapboxgl from 'mapbox-gl'
import { useMapboxMapContext } from '../contexts/MapboxMapContext'
import { POIResult } from '@/shared/api/routes'

export interface UsePOIMarkersProps {
  poiResults: POIResult[]
  selectedPOI?: POIResult | null
  onPOIClick?: (poi: POIResult) => void
}

/**
 * Get SVG path for POI icon based on type
 * Paths match lucide-react icons
 */
function getPOIIconPath(poiType: string): string {
  switch (poiType) {
    case 'gas':
      // Fuel icon - gas pump
      return '<path d="M19 7h1a2 2 0 0 1 2 2v1a2 2 0 0 1-2 2h-1v2a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h1V5a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v2Z"></path><line x1="12" y1="12" x2="12" y2="18"></line><line x1="9" y1="15" x2="15" y2="15"></line>'
    case 'water':
      // Droplet icon - water drop
      return '<path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0Z"></path>'
    case 'rest_stop':
      // SquareParking icon - parking/rest area
      return '<rect width="18" height="18" x="3" y="3" rx="2"></rect><path d="M9 17V7h4a3 3 0 0 1 0 6H9"></path>'
    default:
      // Default MapPin icon
      return '<path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"></path><circle cx="12" cy="10" r="3"></circle>'
  }
}

/**
 * Get background color for POI type
 */
function getPOIBackgroundColor(poiType: string): string {
  switch (poiType) {
    case 'gas':
      return '#dc2626' // Red for gas stations
    case 'water':
      return '#2563eb' // Blue for water
    case 'rest_stop':
      return '#16a34a' // Green for rest stops
    default:
      return '#5C8DC5' // Default brand blue
  }
}

/**
 * Hook for displaying custom icon markers for POI discovery results
 * Shows appropriate icons (Fuel, Droplet, Parking) based on POI type
 */
export function usePOIMarkers({
  poiResults = [],
  selectedPOI,
  onPOIClick
}: UsePOIMarkersProps) {
  const { map, isLoaded, styleLoaded } = useMapboxMapContext()
  const markersRef = useRef<mapboxgl.Marker[]>([])
  const onPOIClickRef = useRef(onPOIClick)
  
  // Store callback in ref to avoid recreating markers when callback changes
  useEffect(() => {
    onPOIClickRef.current = onPOIClick
  }, [onPOIClick])

  useEffect(() => {
    if (!map.current || !isLoaded || !styleLoaded) {
      // Clear markers if map not ready
      markersRef.current.forEach(marker => {
        try {
          marker.remove()
        } catch (e) {
          // Ignore errors if marker was already removed
        }
      })
      markersRef.current = []
      return
    }

    // Clear existing markers
    markersRef.current.forEach(marker => {
      try {
        marker.remove()
      } catch (e) {
        // Ignore errors if marker was already removed
      }
    })
    markersRef.current = []

    // Create custom icon markers for each POI
    poiResults.forEach((poi) => {
      const lat = poi.latitude
      const lng = poi.longitude

      if (lat == null || lng == null || isNaN(Number(lat)) || isNaN(Number(lng))) {
        return
      }

      const isSelected = selectedPOI?.id === poi.id
      const poiType = poi.poi_type || poi.category || 'custom'
      const iconPath = getPOIIconPath(poiType)
      const backgroundColor = getPOIBackgroundColor(poiType)

      // Create marker element with icon
      const markerEl = document.createElement('div')
      markerEl.className = 'poi-marker'
      
      // Different sizes for selected vs normal
      const iconSize = isSelected ? 28 : 24
      const borderWidth = isSelected ? 3 : 2
      const shadowSize = isSelected ? '0 4px 8px rgba(0,0,0,0.4)' : '0 2px 4px rgba(0,0,0,0.3)'
      
      markerEl.innerHTML = `
        <div style="
          width: ${iconSize}px;
          height: ${iconSize}px;
          background-color: ${backgroundColor};
          border-radius: 50%;
          border: ${borderWidth}px solid white;
          box-shadow: ${shadowSize};
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.2s ease-in-out;
        ">
          <svg width="${iconSize - 8}px" height="${iconSize - 8}px" viewBox="0 0 24 24" fill="white" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            ${iconPath}
          </svg>
        </div>
      `
      
      // Add click handler
      markerEl.addEventListener('click', (e) => {
        e.stopPropagation()
        e.preventDefault()
        if (onPOIClickRef.current) {
          onPOIClickRef.current(poi)
        }
      })

      const marker = new mapboxgl.Marker({
        element: markerEl,
        anchor: 'center'
      })
        .setLngLat([lng, lat])
        .addTo(map.current!)

      markersRef.current.push(marker)
    })

    return () => {
      markersRef.current.forEach(marker => {
        try {
          marker.remove()
        } catch (e) {
          // Ignore errors if marker was already removed
        }
      })
      markersRef.current = []
    }
  }, [map, isLoaded, styleLoaded, poiResults, selectedPOI])
}

